<template>
  <div class="temp-email-card">
    <div v-if="isLoadingEmail" class="loading-email">
      <div class="typing-indicator"><span></span><span></span><span></span></div>
      <span style="margin-left: 8px;">{{ $t('tempEmailCard.loadingEmail') }}</span>
    </div>
    <div v-else-if="errorMsg" class="error-email">
      <span style="color: #E53E3E;">{{ errorMsg }}</span>
    </div>
    <div v-else class="temp-email-content">
      <div class="email-header">
        <div class="header-top">
          <h3 class="header-title">{{ $t('tempEmailCard.title') }}</h3>
          <span v-if="tempEmailInfo?.endtime" class="endtime-badge">
            {{ $t('tempEmailCard.validUntil', { time: tempEmailInfo.endtime }) }}
          </span>
        </div>
        <div class="email-address-box">
          <span class="email-address">{{ tempEmailInfo?.mail }}</span>
          <div class="action-buttons">
            <button @click="$emit('copy', tempEmailInfo?.mail)" class="copy-btn">{{ $t('tempEmailCard.copy') }}</button>
            <button @click="toggleAutoFill" class="auto-fill-btn" :class="{ active: isAutoFillEnabled }">
              {{ $t(isAutoFillEnabled ? 'tempEmailCard.autoFillActive' : 'tempEmailCard.autoFill') }}
            </button>
          </div>
        </div>
        <p class="email-pwd-hint">{{ $t('tempEmailCard.password', { password: tempEmailInfo?.pwd }) }}</p>
      </div>
      
      <div class="email-inbox" style="margin-top:20px;">
        <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #eee; padding-bottom:8px; margin-bottom:10px;">
          <h4 style="margin:0; font-size:15px;">{{ $t('tempEmailCard.inbox', { count: tempEmailList ? tempEmailList.length : 0 }) }}</h4>
          <button @click="refreshEmailList" class="refresh-btn" style="background:transparent; border:1px solid #ddd; padding:4px 8px; border-radius:4px; cursor:pointer; font-size:12px;" :disabled="isRefreshing">
            {{ $t(isRefreshing ? 'tempEmailCard.refreshing' : 'tempEmailCard.refresh') }}
          </button>
        </div>
        
        <div v-if="!tempEmailList || tempEmailList.length === 0" class="empty-inbox" style="text-align:center; color:#999; padding:20px 0; font-size:13px;">
          {{ $t('tempEmailCard.waitingEmails') }}
        </div>
        <div v-else class="email-list" style="display:flex; flex-direction:column; gap:12px; max-height: 300px; overflow-y: auto;">
          <div v-for="(email, eIndex) in tempEmailList" :key="eIndex" class="email-item" style="background:#f9f9f9; padding:12px; border-radius:8px; border:1px solid #eee;">
            <div class="email-item-header" style="display:flex; justify-content:space-between; font-size:12px; color:#666; margin-bottom:6px;">
              <span class="email-sender" style="font-weight:bold;">{{ email.fromname || email.frommail }}</span>
              <span class="email-time">{{ email.time2 }}</span>
            </div>
            <div class="email-subject" style="font-size:14px; font-weight:bold; margin-bottom:8px; color:#333;">{{ email.subject }}</div>
            
            <div v-if="email.extractedCode" style="margin-bottom:12px; background:#E6FFFA; border:1px solid #B2F5EA; padding:8px 12px; border-radius:6px; display:flex; justify-content:space-between; align-items:center;">
              <div>
                <span style="font-size:12px; color:#38A169;">{{ $t('tempEmailCard.detectedCode') }}</span>
                <span style="font-size:18px; font-weight:bold; color:#276749; margin-left:6px; letter-spacing:1px;">{{ email.extractedCode }}</span>
              </div>
              <button @click="$emit('copy', email.extractedCode)" style="background:#38A169; color:#fff; border:none; padding:4px 10px; border-radius:4px; cursor:pointer; font-size:12px;">{{ $t('tempEmailCard.copyCode') }}</button>
            </div>
            
            <div class="email-body" v-html="email.content" style="font-size:13px; color:#444; word-break: break-all; overflow:hidden;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TempEmailCard',
  props: {
    msg: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isLoadingEmail: true,
      tempEmailInfo: null,
      tempEmailList: [],
      errorMsg: '',
      isRefreshing: false,
      pollTimer: null,
      isAutoFillEnabled: false,
      lastAutoFilledCode: null
    };
  },
  mounted() {
    this.initTempEmail();
  },
  beforeDestroy() {
    this.clearPollTimer();
  },
  methods: {
    async initTempEmail() {
      try {
        const res = await fetch('https://wm.ktllq.cn/Aibrowser/generateTempEmail');
        const data = await res.json();
        if (data.code === 200 && data.mail) {
          this.tempEmailInfo = data;
          this.isLoadingEmail = false;
          this.startPolling();
        } else {
          this.handleError(this.$t('tempEmailCard.generationFailed'));
        }
      } catch (e) {
        this.handleError(this.$t('tempEmailCard.generationFailed'));
      }
    },
    handleError(msg) {
      this.isLoadingEmail = false;
      this.errorMsg = msg;
    },
    async refreshEmailList() {
      if (!this.tempEmailInfo || this.isRefreshing) return;
      this.isRefreshing = true;
      try {
        const res = await fetch(`https://wm.ktllq.cn/Aibrowser/getTempEmailList?mail=${this.tempEmailInfo.mail}&pwd=${this.tempEmailInfo.pwd}`);
        const data = await res.json();
        if (data.code === 200 && data.data) {
          // 处理邮件内容，尝试提取验证码
          let hasNewCodeToFill = false;
          let latestCode = null;

          this.tempEmailList = data.data.map(email => {
            const extractedCode = this.extractVerificationCode(email.subject + " " + email.content);
            if (extractedCode) {
               latestCode = extractedCode;
            }
            return {
              ...email,
              extractedCode: extractedCode
            };
          });

          // 如果开启了自动填入，并且提取到了验证码，则发送填充验证码指令
          if (this.isAutoFillEnabled && latestCode) {
             // 防止多次重复发送相同验证码，可以通过判断上次发送的验证码来避免
             if (this.lastAutoFilledCode !== latestCode) {
                 this.lastAutoFilledCode = latestCode;
                 this.sendCommandToPage(this.$t('tempEmailCard.fillCodeCommand', { code: latestCode }));
              }
          }
        }
      } catch (e) {
        console.error('Refresh email failed:', e);
      } finally {
        this.isRefreshing = false;
      }
    },
    extractVerificationCode(text) {
      if (!text) return null;
      // 移除 HTML 标签，避免标签内的数字被误认为是验证码
      const cleanText = text.replace(/<[^>]*>?/gm, '');
      
      // 常见验证码正则：匹配“验证码是: 123456”或单纯连续的 4-8 位数字
      // 先尝试匹配带有“验证码”、“code”等前缀的
      const prefixRegex = /(?:验证码|code|校验码|动态码)(?:[:：\s]*)([a-zA-Z0-9]{4,8})/i;
      const prefixMatch = cleanText.match(prefixRegex);
      if (prefixMatch && prefixMatch[1]) {
        return prefixMatch[1];
      }
      
      // 如果没有前缀，尝试寻找单纯的 4 到 8 位连续数字，前后不能有其他数字
      const numberRegex = /(?<!\d)(\d{4,8})(?!\d)/g;
      const numberMatches = cleanText.match(numberRegex);
      if (numberMatches) {
        // 如果有多个匹配，假设最后一个或者是长度为 6/4 的最可能是验证码
        for (let num of numberMatches) {
          if (num.length === 6 || num.length === 4) {
            return num;
          }
        }
        return numberMatches[0];
      }
      return null;
    },
    toggleAutoFill() {
      this.isAutoFillEnabled = !this.isAutoFillEnabled;
      if (this.isAutoFillEnabled && this.tempEmailInfo?.mail) {
        // 发送原生协议指令填充邮箱，并追加处理密码、重复密码及获取验证码的指令
        const commandText = this.$t('tempEmailCard.fillFormCommand', {
          email: this.tempEmailInfo.mail,
          password: this.tempEmailInfo.pwd
        });
        this.sendCommandToPage(commandText);
      }
    },
    sendCommandToPage(command) {
      // 兼容现有的原生通信协议，触发 AiHelpView 中的 injectPageAgentAndExecute
      const encodedCommand = encodeURIComponent(command);
      const url = `aihelp://page_agent_action?command=${encodedCommand}`;
      location.href = url;
   
    },
    startPolling() {
      this.clearPollTimer();
      const poll = async () => {
        await this.refreshEmailList();
        // 收到第一封邮件后停止自动刷新
        if (this.tempEmailList && this.tempEmailList.length > 0) {
          this.clearPollTimer();
        } else {
          this.pollTimer = setTimeout(poll, 10000);
        }
      };
      this.pollTimer = setTimeout(poll, 10000); // 首次延迟10秒，因为刚生成肯定没邮件
    },
    clearPollTimer() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }
}
</script>

<style scoped>
.temp-email-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin: 8px 0;
  width: 100%;
  box-sizing: border-box;
}

.loading-email {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 14px;
}

.typing-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.typing-indicator > span {
  width: 4px;
  height: 4px;
  background-color: currentColor;
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator > span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator > span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.refresh-btn:hover {
  background: #f0f0f0 !important;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.header-title {
  margin-top: 0;
  margin-bottom: 0;
  color: #333;
  font-size: 16px;
}

.endtime-badge {
  font-size: 12px;
  color: #E53E3E;
  background: #FFF5F5;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
}

.email-address-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f5f7fa;
  padding: 10px;
  border-radius: 8px;
  margin: 10px 0;
  flex-direction: column;
  align-items: stretch;
}

.email-address {
  font-weight: bold;
  color: #1C1C1E;
  font-size: 18px;
  word-break: break-all;
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 4px;
}

.copy-btn, .auto-fill-btn {
  background: #6E88F9;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}

.copy-btn:hover, .auto-fill-btn:hover {
  background: #5c74e0 !important;
}

.auto-fill-btn.active {
  background: #38A169;
}

.auto-fill-btn.active:hover {
  background: #2F855A !important;
}

.email-pwd-hint {
  font-size: 12px;
  color: #888;
  margin: 0;
}

/* Customize scrollbar for email list */
.email-list::-webkit-scrollbar {
  width: 6px;
}
.email-list::-webkit-scrollbar-track {
  background: transparent;
}
.email-list::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}
.email-list::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
</style>
